<script lang="ts">
	import type { SessionModel } from '@24hbs/utils';
	import { isValidDate, dateFullMonthFormat } from '$lib/utils/formatter';
	import {
		lessonModeTooltip,
		lessionModeText,
		lessonModeIcon,
		isMarconiLaureaSession
	} from '$lib/utils/course-utils';
	import { LaureaUtils } from '$lib/utils/laurea-utils';
	import { Icon } from '$lib/components/atoms/Icon';
	import { Tooltip, TooltipDesktop } from '$lib/components/atoms/Tooltip';
  import { HourDuration } from '$lib/components/atoms/HourDuration';
	import { onMount } from 'svelte';

	export let session: SessionModel;
	export let showLessonMode: boolean = true;

	let isLaureaSession: boolean = false;

	onMount(() => {
		if (session) {
			isLaureaSession = LaureaUtils.isCourseOfTypeLaurea(session, fetch);
		}
	});
</script>

<!-- Add course class to enable custom color -->
<ul class="course-data space-y-5">
	{#if session && session.startDate && isValidDate(session.startDate)}
		<li class="course-data-item">
			<span class="data-icon">
				<Icon icon="flag" />
			</span>
			<dl class="data-definition">
				<dt class="data-key">Data di inizio</dt>
				<dd class="data-value">
					{#if isMarconiLaureaSession(session) || isLaureaSession}
						Le lezioni saranno disponibili dopo l'immatricolazione accedendo a myunimarconi
					{:else if session.lessonsAvailableImmediately}
						Lezioni disponibili da subito
					{:else}
						{dateFullMonthFormat(session.startDate)}
					{/if}
				</dd>
			</dl>
		</li>
	{/if}
	{#if session && session.duration && session.durationType}
		<li class="course-data-item">
			<span class="data-icon">
				<Icon icon="duration" />
			</span>
			<span class="data-text">
        {#if session.hourDuration}
          <HourDuration hourDuration={session.hourDuration} hasDuration={session.duration ? true : false} />          
        {/if}
				{session.duration}
				{session.durationType}
			</span>
		</li>
	{/if}
	{#if session && session.lessionMode && session.lessionMode.toLowerCase() != 'null' && showLessonMode}
		<li class="course-data-item">
			<span class="data-icon">
				<Icon icon={lessonModeIcon(session.lessionMode)} />
			</span>
			<dl class="data-definition">
				<dt class="data-key">{lessionModeText(session.lessionMode)}</dt>
				{#if session.lessionTimeDetail && session.lessionTimeDetail != '' && session.lessionTimeDetail != 0}
					<dd class="data-value">{session.lessionTimeDetail}</dd>
				{/if}
			</dl>
			{#if lessonModeTooltip(session.lessionMode) !== ''}
				<span class="data-icon info">
					<div class="block md:hidden">
						<Tooltip
							icon={lessonModeIcon(session.lessionMode)}
							title={lessionModeText(session.lessionMode)}
							message={lessonModeTooltip(session.lessionMode)}
						>
							<Icon icon="info" size="middle" />
						</Tooltip>
					</div>
					<div class="hidden md:block">
						<TooltipDesktop
							position="top-left"
							icon={lessonModeIcon(session.lessionMode)}
							title={lessionModeText(session.lessionMode)}
							message={lessonModeTooltip(session.lessionMode)}
						/>
					</div>
				</span>
			{/if}
		</li>
	{/if}

	{#if session && session.certification && session.certification.toLowerCase() != 'null'}
		<li class="course-data-item">
			<span class="data-icon">
				<Icon icon="certificate" />
			</span>
			<dl class="data-definition">
				<dt class="data-key">Titolo conseguito</dt>
				<dd class="data-value">{session.certification}</dd>
			</dl>
		</li>
	{/if}

	{#if session && session.regionAccreditation && session.regionAccreditation !== ''}
		<li class="course-data-item">
			<span class="data-icon">
				<Icon icon="certificate" />
			</span>
			<dl class="data-definition content-with-tooltip">
				<dt class="data-key">Attestato frequenza regione {session.regionAccreditation}</dt>
				{#if session.idCourseForAccreditation && session.idCourseForAccreditation !== ''}
					<dd class="data-value uppercase">id corso {session.idCourseForAccreditation}</dd>
				{/if}
			</dl>
			<span class="data-icon info">
				<div class="block md:hidden">
					<Tooltip
						icon="certificate"
						title={`Attestato frequenza Regione ${session.regionAccreditation}`}
						message="Il rilascio richiede la frequenza minima del 75% delle lezioni e iscrizione prima della prima data prevista a calendario."
					>
						<Icon icon="info" size="middle" />
					</Tooltip>
				</div>
				<div class="hidden md:block">
					<TooltipDesktop
						position="top-left"
						icon="certificate"
						title={`Attestato frequenza Regione ${session.regionAccreditation}`}
						message="Il rilascio richiede la frequenza minima del 75% delle lezioni e iscrizione prima della prima data prevista a calendario."
					/>
				</div>
			</span>
		</li>
	{/if}

	{#if session && session.stage}
		<li class="course-data-item">
			<span class="data-icon">
				<Icon icon="stage" />
			</span>
			<span class="data-text"> STAGE GARANTITO</span>
		</li>
	{/if}

	{#if session && session.placement}
		<li class="course-data-item">
			<span class="data-icon">
				<Icon icon="stage" />
			</span>
			<span class="data-text"> CAREER SERVICE </span>
		</li>
	{/if}

	{#if session && session.productCode && session.productCode.toLowerCase() != 'null'}
		<li class="course-data-item">
			<span class="data-icon">
				<Icon icon="code" />
			</span>
			<dl class="data-definition">
				<dt class="data-key">Codice iniziativa</dt>
				<dd class="data-value">{session.productCode}</dd>
			</dl>
		</li>
	{/if}
</ul>

<style type="text/scss" lang="scss" scoped>
	.course-data-item {
		@apply flex items-center;
		@apply text-gray-900;
	}

	.data-icon {
		@apply inline-flex flex-shrink-0 self-start mr-3;

		&.info {
			@apply flex;
			@apply ml-2;
			@apply text-courses-default;
			@apply cursor-pointer;
		}
	}

	.data-text {
		@apply flex-shrink-0;
		@apply uppercase tracking-wide text-xs md:text-sm;
	}

	.data-key {
		@apply uppercase tracking-wide text-xs md:text-sm;
	}

	.data-value {
		@apply text-xs md:text-sm;
	}

	.data-key + .data-value {
		@apply mt-1;
	}

	.content-with-tooltip {
		@apply w-3/5 md:w-auto;
	}
</style>
